// NOTA: los imports de este archivo llevan extension .js a proposito. Vite
// resuelve sin extension, pero `node --test` no.
//
// Logica pura de la pestana Caja y turnos de Reportes Inteligentes.
//
// EL PUNTO CLAVE DE ESTE ARCHIVO — por que no se muestra `diferencia` a secas:
//
// Al cerrar, Caja.jsx calcula por CADA metodo de pago un teorico (saldo inicial
// + ventas de ese metodo + movimientos) y lo compara contra lo que el cajero
// declaro. `turnos_caja.diferencia` es la suma de esas diferencias, de todos los
// metodos juntos. Si el cajero cuenta solo el efectivo y deja tarjeta/QR/
// transferencia en blanco, esos metodos entran con real = 0 contra un teorico
// igual a sus ventas, y el turno arrastra un negativo enorme que NO es faltante
// de caja: es un arqueo sin terminar.
//
// Medido el 16/09/2026 sobre la DB de produccion (90 dias): en la organizacion
// de mas volumen, 232 de 240 cierres tenian al menos un metodo con ventas
// declarado en cero. El desvio absoluto promedio de esos turnos era $257.636;
// el de los 8 arqueos completos, $2.538 — cien veces menos. Presentar la suma
// cruda como "faltante" le diria al dueno que perdio decenas de millones.
//
// Por eso todo lo que agrega plata acá separa arqueos completos de incompletos,
// y el desvio solo se totaliza sobre los completos.

const TOLERANCIA = 0.01;

/**
 * Clasifica el arqueo de un turno leyendo `resumen_cierre` (jsonb con una fila
 * por metodo: { nombre, teorico, real, diferencia }).
 *
 * - `completo`: todo metodo que tuvo movimiento fue declarado.
 * - `metodosSinDeclarar`: los que tenian teorico > 0 y se declararon en 0. Son
 *   los que inflan la diferencia del turno.
 * - `sinResumen`: cierres viejos anteriores a que se persistiera el resumen; no
 *   se puede afirmar nada de ellos, asi que no cuentan como completos.
 */
export const clasificarArqueo = (turno) => {
  const resumen = Array.isArray(turno?.resumen_cierre) ? turno.resumen_cierre : [];
  if (!resumen.length) {
    return { completo: false, sinResumen: true, metodosSinDeclarar: [] };
  }

  const metodosSinDeclarar = resumen
    .filter(
      (m) =>
        Number(m?.teorico || 0) > TOLERANCIA &&
        Math.abs(Number(m?.real || 0)) < TOLERANCIA,
    )
    .map((m) => String(m?.nombre || 'Sin nombre'));

  return {
    completo: metodosSinDeclarar.length === 0,
    sinResumen: false,
    metodosSinDeclarar,
  };
};

/** Un turno cuenta como cerrado solo si el estado Y la fecha lo dicen. */
const estaCerrado = (t) =>
  String(t?.estado || '').toLowerCase() === 'cerrado' && Boolean(t?.fecha_cierre);

/**
 * Normaliza los turnos del periodo para la tabla de arqueos.
 * `siteNameById` es un Map(String(id) -> nombre).
 */
export const computeArqueos = ({ turnos, siteNameById }) => {
  const nombreSitio = (id) =>
    (id && siteNameById?.get?.(String(id))) || 'Sin sucursal';

  return (turnos || [])
    .map((t) => {
      const arqueo = clasificarArqueo(t);
      const cerrado = estaCerrado(t);
      return {
        id: t.id,
        sucursal: nombreSitio(t.sitio_id),
        usuario: String(t.usuario_email || '').trim() || 'Sin usuario',
        apertura: t.fecha_apertura,
        cierre: t.fecha_cierre,
        cerrado,
        saldoInicial: Number(t.saldo_inicial || 0),
        declarado: Number(t.total_real || 0),
        diferencia: Number(t.diferencia || 0),
        ingresos: Number(t.total_ingresos || 0),
        egresos: Number(t.total_egresos || 0),
        gastos: Number(t.total_gastos || 0),
        ...arqueo,
        // Solo un arqueo completo y cerrado permite leer la diferencia como
        // desvio de caja. En el resto es ruido de un conteo sin terminar.
        desvioConfiable: cerrado && arqueo.completo,
      };
    })
    .sort((a, b) => String(b.apertura || '').localeCompare(String(a.apertura || '')));
};

/** Totales del periodo, separando lo que se puede afirmar de lo que no. */
export const computeResumenArqueos = (arqueos) => {
  const filas = arqueos || [];
  const confiables = filas.filter((a) => a.desvioConfiable);
  const incompletos = filas.filter((a) => a.cerrado && !a.completo);

  const desvioNeto = confiables.reduce((acc, a) => acc + a.diferencia, 0);
  const desvioAbs = confiables.reduce((acc, a) => acc + Math.abs(a.diferencia), 0);

  return {
    turnos: filas.length,
    cerrados: filas.filter((a) => a.cerrado).length,
    abiertos: filas.filter((a) => !a.cerrado).length,
    arqueosCompletos: confiables.length,
    arqueosIncompletos: incompletos.length,
    // Los metodos que mas veces quedan sin declarar: es el dato accionable
    // cuando el proceso de cierre esta incompleto.
    metodosMasOmitidos: contarMetodosOmitidos(incompletos),
    desvioNeto,
    desvioPromedio: confiables.length ? desvioAbs / confiables.length : 0,
  };
};

const contarMetodosOmitidos = (arqueos) => {
  const map = new Map();
  (arqueos || []).forEach((a) => {
    a.metodosSinDeclarar.forEach((m) => map.set(m, (map.get(m) || 0) + 1));
  });
  return Array.from(map.entries())
    .map(([nombre, veces]) => ({ nombre, veces }))
    .sort((a, b) => b.veces - a.veces);
};

/**
 * Agrupa por cajero. `turnos_caja` no tiene FK a usuarios: guarda
 * `usuario_email` como texto libre, asi que la clave es ese string normalizado
 * (un mismo usuario puede aparecer como email y como nombre completo segun como
 * estuviera cargado el profile al abrir el turno).
 */
export const computeDesvioPorCajero = (arqueos) => {
  const map = new Map();

  (arqueos || []).forEach((a) => {
    const key = a.usuario.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        usuario: a.usuario,
        turnos: 0,
        arqueosCompletos: 0,
        arqueosIncompletos: 0,
        desvioNeto: 0,
        desvioAbs: 0,
        ventas: 0,
        tickets: 0,
      });
    }
    const e = map.get(key);
    e.turnos += 1;
    e.ventas += Number(a.ventas || 0);
    e.tickets += Number(a.tickets || 0);
    if (a.desvioConfiable) {
      e.arqueosCompletos += 1;
      e.desvioNeto += a.diferencia;
      e.desvioAbs += Math.abs(a.diferencia);
    } else if (a.cerrado) {
      e.arqueosIncompletos += 1;
    }
  });

  return Array.from(map.values())
    .map((e) => ({
      ...e,
      desvioPromedio: e.arqueosCompletos ? e.desvioAbs / e.arqueosCompletos : 0,
    }))
    .sort((a, b) => b.turnos - a.turnos);
};

/**
 * Atribuye cada venta al turno cuya ventana la contiene.
 *
 * Por que por ventana y no por `sales.user_id`: medido el 16/09/2026, dos de
 * las tres organizaciones activas tienen user_id NULL en el 100% de sus ventas
 * (depende de que `profile.user_id` este cargado al vender), mientras que el
 * 99,6% de esas mismas ventas cae dentro de la ventana de un turno. El turno es
 * ademas la unidad correcta de responsabilidad para un reporte de caja.
 *
 * Un turno abierto se considera vigente hasta `ahora`.
 */
export const atribuirVentasATurnos = ({ turnos, sales, ahora = Date.now() }) => {
  const ventanas = (turnos || []).map((t) => ({
    id: t.id,
    sitio: t.sitio_id ? String(t.sitio_id) : null,
    desde: new Date(t.apertura ?? t.fecha_apertura).getTime(),
    hasta: (t.cierre ?? t.fecha_cierre)
      ? new Date(t.cierre ?? t.fecha_cierre).getTime()
      : ahora,
  })).filter((v) => Number.isFinite(v.desde));

  const totales = new Map();

  (sales || []).forEach((s) => {
    // Venta administrativa: no paso por ningun turno de caja.
    if (s?.afecta_caja === false) return;
    const ts = new Date(s?.created_at).getTime();
    if (!Number.isFinite(ts)) return;
    const sitio = s?.site_id ? String(s.site_id) : null;

    // Si la venta no trae sucursal, o el turno no la tiene, no se usa ese
    // criterio para descartar: perder la venta seria peor que atribuirla al
    // unico turno que la contiene en el tiempo.
    const v = ventanas.find(
      (w) =>
        ts >= w.desde &&
        ts <= w.hasta &&
        (!w.sitio || !sitio || w.sitio === sitio),
    );
    if (!v) return;

    const e = totales.get(v.id) || { ventas: 0, tickets: 0 };
    e.ventas += Number(s?.total || 0);
    e.tickets += 1;
    totales.set(v.id, e);
  });

  return totales;
};

export const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/**
 * Mapa de calor de ventas por dia de semana y hora.
 *
 * La hora sale de getters UTC a proposito: el POS guarda `created_at` con hora
 * forjada (hora local + sufijo Z). Con getters locales, en Argentina todo se
 * correria tres horas y el pico de la tarde aparecería al mediodia.
 *
 * Se promedia por la cantidad de veces que cada dia de semana ocurre en el
 * rango: un periodo de 30 dias tiene 4 lunes y 5 martes, y sin normalizar el
 * martes se veria mas fuerte solo por eso.
 */
export const computeVentasPorHora = ({ sales, dateFrom, dateTo }) => {
  const celdas = new Map();
  const ocurrencias = new Array(7).fill(0);

  const cursor = new Date(`${dateFrom}T12:00:00`);
  const fin = new Date(`${dateTo}T12:00:00`);
  let guarda = 0;
  while (cursor <= fin && guarda < 4000) {
    ocurrencias[cursor.getDay()] += 1;
    cursor.setDate(cursor.getDate() + 1);
    guarda += 1;
  }

  (sales || []).forEach((s) => {
    const d = new Date(s?.created_at);
    if (Number.isNaN(d.getTime())) return;
    const dia = d.getUTCDay();
    const hora = d.getUTCHours();
    const key = `${dia}-${hora}`;
    const e = celdas.get(key) || { dia, hora, ventas: 0, tickets: 0 };
    e.ventas += Number(s?.total || 0);
    e.tickets += 1;
    celdas.set(key, e);
  });

  const filas = Array.from(celdas.values()).map((c) => ({
    ...c,
    // Promedio por ocurrencia del dia: comparable entre dias del rango.
    ventasPromedio: ocurrencias[c.dia] ? c.ventas / ocurrencias[c.dia] : c.ventas,
    ticketsPromedio: ocurrencias[c.dia] ? c.tickets / ocurrencias[c.dia] : c.tickets,
  }));

  const maxPromedio = filas.reduce((m, c) => Math.max(m, c.ventasPromedio), 0);

  // Horas con actividad, para no dibujar 24 columnas cuando el local abre 10.
  const horasActivas = Array.from(new Set(filas.filter((c) => c.tickets > 0).map((c) => c.hora)))
    .sort((a, b) => a - b);

  return { celdas: filas, maxPromedio, horasActivas, ocurrencias };
};
